/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ButtonIntrinsicProps } from './toggle.types';
import { useButtonSetContext } from '#hooks/UNSAFE_useButtonSetContext';
import { ButtonSetButton } from '#UNSAFE_ButtonSetButton';
import { ButtonSetIconButton } from '#UNSAFE_ButtonSetIconButton';
import { ComponentProps } from 'preact';
import { ToggleButtonLabel } from '#UNSAFE_ToggleButtonLabel';
import { generateId } from '#utils/PRIVATE_id';

type ButtonSetContextProps = ReturnType<typeof useButtonSetContext>;
type ButtonSetButtonProps = ComponentProps<typeof ButtonSetButton>;
type ButtonSetIconButtonProps = ComponentProps<typeof ButtonSetIconButton>;

/**
 *
 * @param buttonProps
 * @returns globalEventProps that can be spread onto target element
 */
export function processGlobals(buttonProps: ButtonIntrinsicProps): {
  globalEventProps: ButtonIntrinsicProps;
} {
  const globalEventProps = {
    onBlur: buttonProps.onBlur,
    onFocus: buttonProps.onFocus,
    onMouseEnter: buttonProps.onMouseEnter,
    onMouseLeave: buttonProps.onMouseLeave,
    onTouchEnd: buttonProps.onTouchEnd,
    onTouchStart: buttonProps.onTouchStart
  };

  return {
    globalEventProps
  };
}

/**
 * Merge buttonset props with toggle props for spreadable properties and flags
 * @param toggleProps
 * @param buttonSetProps
 * @returns
 */
export function processSetButtonProps(
  toggleProps: (ButtonSetButtonProps | ButtonSetIconButtonProps) & ButtonIntrinsicProps,
  buttonSetProps: ButtonSetContextProps
): {
  isDisabled: boolean | undefined;
  buttonSetButtonProps: ButtonSetButtonProps | ButtonSetIconButtonProps;
  inputProps: ComponentProps<typeof ToggleButtonLabel>;
  globalEventProps: ButtonIntrinsicProps;
} {
  const isDisabled = toggleProps.isDisabled ?? buttonSetProps.isDisabled ? true : undefined;

  const commonProps = {
    size: toggleProps.size ?? buttonSetProps.size,
    isDisabled: isDisabled
  };
  const buttonProps = {
    variant: toggleProps.variant ?? buttonSetProps.variant,
    ...commonProps
  };

  const buttonSetButtonProps = {
    layoutWidth: toggleProps.layoutWidth ?? buttonSetProps.layoutWidth,
    ...buttonProps
  };

  const inputProps = {
    inputType: buttonSetProps.inputType ?? toggleProps.inputType ?? 'checkbox',
    inputName: buttonSetProps.inputName ?? toggleProps.inputName ?? generateId(),
    ...commonProps
  };

  const { globalEventProps } = processGlobals(toggleProps);

  return {
    isDisabled,
    buttonSetButtonProps,
    inputProps,
    globalEventProps
  };
}
